import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { View, Platform, useWindowDimensions, Pressable, Text, StyleSheet } from 'react-native';
import Modal from 'react-native-modal';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { Title } from '@constants/Themed';
import { BlurView } from '@react-native-community/blur';
import { LinearGradient } from 'expo-linear-gradient';
import * as Animatable from 'react-native-animatable';
import Button from '../../UI/Button/Button';
import { HIDE_FIRST_TUTORIAL, SHOW_FIRST_TUTORIAL, SET_TUTORIAL_COMPLETE, OPEN_MENU, OPEN_MESSAGES, CLOSE_MENU, CLOSE_MESSAGES, OPEN_WARPER, CLOSE_WARPER, TOGGLE_EASY_MODE } from '@reduxLocal/actions/types';
import apis from '@api/api';
import { XStack } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';

// Utility function to get element position
const getElementPosition = (elementId: string): DOMRect | null => {
  try {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return null;
    }
    const element = document.getElementById(elementId);
    if (!element) {
      return null;
    }
    // Check if element is in the DOM and has valid dimensions
    if (!document.body.contains(element)) {
      return null;
    }
    const rect = element.getBoundingClientRect();
    // Return null if element has no dimensions (not rendered yet)
    if (rect.width === 0 && rect.height === 0) {
      return null;
    }
    return rect;
  } catch (error) {
    console.warn('[FirstTutorial] Error getting element position:', error);
    return null;
  }
};

// Calculate tooltip position relative to target element
const calculateTooltipPosition = (
  targetRect: DOMRect,
  position: 'top' | 'bottom' | 'left' | 'right',
  offset: number = 12
): { top: number; left: number; position: 'top' | 'bottom' | 'left' | 'right' } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const tooltipWidth = 300; // Estimated tooltip width
  const tooltipHeight = 150; // Estimated tooltip height

  let top = 0;
  let left = 0;
  let finalPosition: 'top' | 'bottom' | 'left' | 'right' = position;

  switch (position) {
    case 'top':
      top = targetRect.top - tooltipHeight - offset;
      left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
      // If tooltip goes off top, switch to bottom
      if (top < 0) {
        finalPosition = 'bottom';
        top = targetRect.bottom + offset;
      }
      break;
    case 'bottom':
      top = targetRect.bottom + offset;
      left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
      // If tooltip goes off bottom, switch to top
      if (top + tooltipHeight > viewportHeight) {
        finalPosition = 'top';
        top = targetRect.top - tooltipHeight - offset;
      }
      break;
    case 'left':
      top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
      left = targetRect.left - tooltipWidth - offset;
      // If tooltip goes off left, switch to right
      if (left < 0) {
        finalPosition = 'right';
        left = targetRect.right + offset;
      }
      break;
    case 'right':
      top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
      left = targetRect.right + offset;
      // If tooltip goes off right, switch to left
      if (left + tooltipWidth > viewportWidth) {
        finalPosition = 'left';
        left = targetRect.left - tooltipWidth - offset;
      }
      break;
  }

  // Ensure tooltip stays within viewport bounds
  top = Math.max(0, Math.min(top, viewportHeight - tooltipHeight));
  left = Math.max(0, Math.min(left, viewportWidth - tooltipWidth));

  return { top, left, position: finalPosition };
};

interface Instruction {
  targetElementId: string;
  helpText: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  dispatchType?: string;
  requiresInteraction?: boolean;
}

// Move instructions outside component to prevent recreation on every render
const instructions: Instruction[] = [
  {
    targetElementId: 'tutorial-menu-button',
    helpText: 'Click here to expand or collapse your menu',
    position: 'right',
    dispatchType: 'OPEN_MENU',
    requiresInteraction: false,
  },
  {
    targetElementId: 'tutorial-menu-warper-button',
    helpText: 'Change menu type here',
    position: 'bottom',
    dispatchType: 'OPEN_WARPER',
    requiresInteraction: false,
  },
  {
    targetElementId: 'tutorial-menu-warper-button',
    helpText: 'Select the menu type that best suits your needs and your menu will be transformed',
    position: 'bottom',
    dispatchType: 'CLOSE_WARPER',
    requiresInteraction: false,
  },
  {
    targetElementId: 'tutorial-messages-button',
    helpText: 'Click here to open messages',
    position: 'left',
    dispatchType: 'OPEN_MESSAGES',
    requiresInteraction: false,
  },
  {
    targetElementId: 'tutorial-easy-mode-toggle',
    helpText: 'Toggle between Normal and AI mode to switch interfaces',
    position: 'bottom',
    dispatchType: 'TOGGLE_EASY_MODE',
    requiresInteraction: false,
  },
];

const FirstTutorial = () => {
  const { isTablet } = useDeviceType();

  // Get user's tutorial flag from Redux state
  const userTutorials = useSelector((state: DefaultRootState) => state.promiseData.data.userData.tutorials);
  const modalDisplayed = useSelector((state: DefaultRootState) => state.modals.firstTutorial.modalDisplayed);
  const isAuthenticated = useSelector((state: DefaultRootState) => state.promiseData.isAuthenticated);
  const userData = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const focusMode = useSelector((state: DefaultRootState) => state.UI.focusMode);

  const { width, height } = useWindowDimensions();
  const dispatch = useDispatch();
  const [activeInstruction, setActiveInstruction] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const [isElementVisible, setIsElementVisible] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const tooltipRef = useRef<Animatable.View>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef(false);
  // Use ref to track latest onboarding state to avoid closure issues
  const onboardingInProgressRef = useRef(onboardingInProgress);

  // Only show if user hasn't seen it before AND modal is set to display
  // In dev mode, also show if modalDisplayed is true (for testing)
  // IMPORTANT: Never show during onboarding or focus mode - wait until both are complete
  const shouldShowTutorial = useMemo(() => {
    // Don't show if onboarding is in progress
    if (onboardingInProgress) {
      return false;
    }
    // Don't show if focus mode is active (elements are hidden)
    if (focusMode) {
      return false;
    }
    return __DEV__
      ? modalDisplayed
      : (modalDisplayed && !userTutorials?.firstTutorial);
  }, [modalDisplayed, userTutorials?.firstTutorial, onboardingInProgress, focusMode]);

  // Check if all required tutorial elements are ready
  const checkAllElementsReady = useCallback((): boolean => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return false;
    }

    // Check if at least the first required element exists (menu button)
    // This is the most critical element for the tutorial
    const firstElement = document.getElementById(instructions[0]?.targetElementId);
    if (!firstElement) {
      return false;
    }

    // Check if element has valid dimensions
    const rect = firstElement.getBoundingClientRect();
    const hasValidDimensions = rect.width > 0 && rect.height > 0;
    const isInDocument = document.body.contains(firstElement);

    return hasValidDimensions && isInDocument;
  }, []);

  // Auto-show tutorial for first-time users when they log in
  // Wait for dashboard elements to be ready AND onboarding/focus mode to be complete before showing
  // Focus mode hides elements during onboarding, so we must wait for it to be deactivated
  useEffect(() => {
    // Don't show tutorial if onboarding is in progress - wait for it to complete
    if (onboardingInProgress) {
      console.log('[FirstTutorial] Onboarding in progress, waiting for completion');
      return;
    }

    // Don't show tutorial if focus mode is active - elements are hidden during focus mode
    if (focusMode) {
      console.log('[FirstTutorial] Focus mode active (elements hidden), waiting for deactivation');
      return;
    }

    // Only proceed if user is authenticated, has data, hasn't seen tutorial, and modal isn't already displayed
    if (isAuthenticated && userData && !userTutorials?.firstTutorial && !modalDisplayed) {
      console.log('[FirstTutorial] Onboarding and focus mode complete, first-time user detected, checking element readiness');
      
      let retryCount = 0;
      const maxRetries = 20; // Try for up to 10 seconds (20 * 500ms)
      
      const checkAndShow = () => {
        // Get current state from Redux store to avoid closure issues
        const currentState = store.getState();
        const currentOnboardingInProgress = currentState?.UI?.onboardingInProgress;
        const currentFocusMode = currentState?.UI?.focusMode;

        // Double-check onboarding and focus mode are still not active (safety check)
        if (currentOnboardingInProgress) {
          console.log('[FirstTutorial] Onboarding started during check, aborting');
          return;
        }

        if (currentFocusMode) {
          console.log('[FirstTutorial] Focus mode activated during check, aborting');
          return;
        }

        if (checkAllElementsReady()) {
          console.log('[FirstTutorial] Elements ready, onboarding and focus mode complete, showing tutorial');
          dispatch({ type: SHOW_FIRST_TUTORIAL });
        } else if (retryCount < maxRetries) {
          retryCount++;
          console.log(`[FirstTutorial] Elements not ready, retrying (${retryCount}/${maxRetries})`);
          setTimeout(checkAndShow, 500);
        } else {
          console.warn('[FirstTutorial] Elements not ready after max retries, showing tutorial anyway');
          // Show tutorial anyway after max retries - it will handle missing elements gracefully
          dispatch({ type: SHOW_FIRST_TUTORIAL });
        }
      };

      // Start checking after a short delay to allow dashboard to fully render
      // Elements should now be visible since focus mode is off
      const timer = setTimeout(() => {
        checkAndShow();
      }, 1000); // Short delay just for dashboard rendering
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, userData, userTutorials?.firstTutorial, modalDisplayed, onboardingInProgress, focusMode, dispatch, checkAllElementsReady]);

  // Hide modal if user has already seen the tutorial
  useEffect(() => {
    if (userTutorials?.firstTutorial && modalDisplayed) {
      console.log('[FirstTutorial] User has already seen tutorial, hiding modal');
      if(!__DEV__)
      dispatch({ type: HIDE_FIRST_TUTORIAL });
    }
  }, [userTutorials?.firstTutorial, modalDisplayed, dispatch]);

  // Cleanup function to remove highlights from all elements
  const cleanupHighlights = useCallback(() => {
    if (Platform.OS !== 'web') return;
    instructions.forEach((instruction) => {
      const element = document.getElementById(instruction.targetElementId);
      if (element) {
        element.style.boxShadow = '';
        element.style.zIndex = '';
        element.style.transition = '';
      }
    });
  }, []);

  // Check if element exists and is ready (has valid dimensions and is in DOM)
  const checkElementReady = useCallback((elementId: string): boolean => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return false;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      return false;
    }

    // Use requestAnimationFrame to ensure layout is complete
    const rect = element.getBoundingClientRect();
    const hasValidDimensions = rect.width > 0 && rect.height > 0;
    const isInDocument = document.body.contains(element);

    return hasValidDimensions && isInDocument;
  }, []);

  // Update tooltip position - uses requestAnimationFrame for reliable DOM reads
  const updateTooltipPosition = useCallback((instructionIndex: number, retryCount: number = 0) => {
    try {
      if (Platform.OS !== 'web' || !shouldShowTutorial) {
        return;
      }

      // Clear any pending updates
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }

      const instruction = instructions[instructionIndex];
      if (!instruction) {
        setIsElementVisible(false);
        setTooltipPosition(null);
        return;
      }

      // Use requestAnimationFrame to ensure DOM layout is complete
      requestAnimationFrame(() => {
        // Double RAF for more reliable layout (handles edge cases)
        requestAnimationFrame(() => {
          try {
            if (!checkElementReady(instruction.targetElementId)) {
              // Retry mechanism with exponential backoff
              if (retryCount < 5) {
                const delay = Math.min(100 * Math.pow(2, retryCount), 800);
                updateTimeoutRef.current = setTimeout(() => {
                  updateTooltipPosition(instructionIndex, retryCount + 1);
                }, delay);
              } else {
                console.warn('[FirstTutorial] Element not found after retries:', instruction.targetElementId);
                setIsElementVisible(false);
                setTooltipPosition(null);
              }
              return;
            }

            const targetRect = getElementPosition(instruction.targetElementId);
            if (!targetRect || targetRect.width === 0 || targetRect.height === 0) {
              setIsElementVisible(false);
              setTooltipPosition(null);
              return;
            }

            // Check if element is in viewport (with some tolerance)
            const padding = 50;
            const isVisible =
              targetRect.top >= -padding &&
              targetRect.left >= -padding &&
              targetRect.bottom <= window.innerHeight + padding &&
              targetRect.right <= window.innerWidth + padding;

            setIsElementVisible(isVisible);

            // Calculate tooltip position
            const position = calculateTooltipPosition(targetRect, instruction.position);
            setTooltipPosition({ top: position.top, left: position.left });

            // Highlight the target element
            const element = document.getElementById(instruction.targetElementId);
            if (element) {
              element.style.transition = 'all 0.3s ease';
              element.style.boxShadow = '0 0 0 4px rgba(90, 192, 234, 0.5)';
              element.style.zIndex = '10000';
            }
          } catch (error) {
            console.warn('[FirstTutorial] Error updating tooltip position:', error);
            setIsElementVisible(false);
            setTooltipPosition(null);
          }
        });
      });
    } catch (error) {
      console.warn('[FirstTutorial] Error in updateTooltipPosition:', error);
      setIsElementVisible(false);
      setTooltipPosition(null);
    }
  }, [shouldShowTutorial, checkElementReady]);

  // Initialize tutorial when modal opens
  useEffect(() => {
    if (!shouldShowTutorial) {
      // Reset all state when modal closes
      setIsInitializing(false);
      setTooltipPosition(null);
      setIsElementVisible(false);
      isNavigatingRef.current = false;
      cleanupHighlights();
      return;
    }

    // Initialize when modal opens (only once per open)
    if (shouldShowTutorial && !isInitializing) {
      setIsInitializing(true);
      setActiveInstruction(0);
      setTooltipPosition(null);
      setIsElementVisible(false);
      isNavigatingRef.current = false;
      cleanupHighlights();

      // Wait for DOM to be ready before initializing
      // Use a longer delay on initial load to ensure all components are mounted
      // Also verify element is ready before proceeding
      const initTimer = setTimeout(() => {
        // Double-check element is ready before initializing
        if (checkElementReady(instructions[0]?.targetElementId)) {
          updateTooltipPosition(0);
        } else {
          // Retry initialization if element not ready
          console.log('[FirstTutorial] Element not ready during init, retrying...');
          const retryTimer = setTimeout(() => {
            if (checkElementReady(instructions[0]?.targetElementId)) {
              updateTooltipPosition(0);
            } else {
              console.warn('[FirstTutorial] Element still not ready after retry');
              setIsInitializing(false);
            }
          }, 1000);
          return () => clearTimeout(retryTimer);
        }
      }, 800); // Increased delay to 800ms

      return () => {
        clearTimeout(initTimer);
      };
    }
  }, [shouldShowTutorial, isInitializing, updateTooltipPosition, cleanupHighlights]);

  // Update position when instruction changes
  useEffect(() => {
    if (!shouldShowTutorial || !isInitializing) {
      return;
    }

    // Cleanup previous highlight before updating
    cleanupHighlights();

    // Reset state before updating
    setTooltipPosition(null);
    setIsElementVisible(false);

    // Wait a bit for state to settle and DOM to update, then update position
    const updateTimer = setTimeout(() => {
      updateTooltipPosition(activeInstruction);
    }, 150);

    return () => {
      clearTimeout(updateTimer);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, [activeInstruction, shouldShowTutorial, isInitializing, updateTooltipPosition, cleanupHighlights]);

  // Handle scroll and resize events with debouncing
  useEffect(() => {
    if (!shouldShowTutorial || Platform.OS !== 'web') {
      return;
    }

    let scrollTimeout: NodeJS.Timeout;
    let resizeTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        updateTooltipPosition(activeInstruction);
      }, 150);
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateTooltipPosition(activeInstruction);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(resizeTimeout);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [shouldShowTutorial, activeInstruction, updateTooltipPosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupHighlights();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [cleanupHighlights]);

  const handleNext = () => {
    if (isNavigatingRef.current) return;

    isNavigatingRef.current = true;
    cleanupHighlights();

    if (activeInstruction < instructions.length - 1) {
      setActiveInstruction(prev => prev + 1);
      // Reset navigation lock after state update
      setTimeout(() => {
        isNavigatingRef.current = false;
        tooltipRef.current?.fadeIn?.();
      }, 50);
    } else {
      isNavigatingRef.current = false;
    }
  };

  const handlePrevious = () => {
    if (isNavigatingRef.current) return;

    isNavigatingRef.current = true;
    cleanupHighlights();

    if (activeInstruction > 0) {
      setActiveInstruction(prev => prev - 1);
      // Reset navigation lock after state update
      setTimeout(() => {
        isNavigatingRef.current = false;
        tooltipRef.current?.fadeIn?.();
      }, 50);
    } else {
      isNavigatingRef.current = false;
    }
  };

  const handleDismiss = async () => {
    isNavigatingRef.current = false;
    cleanupHighlights();
    try {
      console.log('[FirstTutorial] Dismissing tutorial and setting flag');
      await apis.hideFirstTutorial();
      // Update Redux state immediately
      dispatch({ type: SET_TUTORIAL_COMPLETE });
      dispatch({ type: HIDE_FIRST_TUTORIAL });
    } catch (error) {
      console.error('[FirstTutorial] Error hiding tutorial:', error);
      // Still hide modal even if API call fails
      dispatch({ type: HIDE_FIRST_TUTORIAL });
    }
  };

  const handleComplete = async () => {
    isNavigatingRef.current = false;
    cleanupHighlights();
    try {
      console.log('[FirstTutorial] Completing tutorial and setting flag');
      await apis.hideFirstTutorial();
      // Update Redux state immediately
      dispatch({ type: SET_TUTORIAL_COMPLETE });
      setActiveInstruction(0);
      dispatch({ type: HIDE_FIRST_TUTORIAL });
    } catch (error) {
      console.error('[FirstTutorial] Error completing tutorial:', error);
      // Still hide modal even if API call fails
      dispatch({ type: SET_TUTORIAL_COMPLETE });
      setActiveInstruction(0);
      dispatch({ type: HIDE_FIRST_TUTORIAL });
    }
  };

  const currentInstruction = instructions[activeInstruction];

  if (!shouldShowTutorial) {
    return null;
  }

  // Early return if onboarding is in progress or focus mode is active - don't render anything
  // Focus mode hides elements, so tutorial can't find them
  if (onboardingInProgress || focusMode) {
    return null;
  }

  return (
    <Modal
      isVisible={shouldShowTutorial}
      deviceHeight={height}
      deviceWidth={width}
      backdropOpacity={0.5}
      style={{ flex: 1 }}
      onModalHide={handleDismiss}
      animationIn="fadeIn"
      animationOut="fadeOut"
    >
      <View style={{ flex: 1 }}>
        {/* Always show backdrop when modal is visible */}
        {Platform.OS === 'web' && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9998,
              pointerEvents: 'none',
            } as any}
          />
        )}

        {/* Debug info in dev mode */}
        {__DEV__ && (
          <View
            style={{
              position: 'absolute',
              top: 20,
              left: 20,
              backgroundColor: 'rgba(255, 0, 0, 0.8)',
              padding: 10,
              borderRadius: 8,
              zIndex: 10001,
            } as any}
          >
            <Text style={{ color: 'white', fontSize: 12 }}>
              Tutorial Active{'\n'}
              Step: {activeInstruction + 1}/{instructions.length}{'\n'}
              Element: {currentInstruction?.targetElementId || 'None'}{'\n'}
              Visible: {isElementVisible ? 'Yes' : 'No'}{'\n'}
              Position: {tooltipPosition ? 'Set' : 'Not set'}{'\n'}
              Initializing: {isInitializing ? 'Yes' : 'No'}
            </Text>
          </View>
        )}

        {/* Tooltip - only show if we have a current instruction and valid position */}
        {currentInstruction && isElementVisible && tooltipPosition && (
          <Animatable.View
            ref={tooltipRef}
            useNativeDriver={false}
            animation="fadeIn"
            duration={300}
            style={{
              position: 'absolute',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              zIndex: 10000,
              maxWidth: 320,
              minWidth: 280,
            } as any}
          >
            <View
              style={{
                overflow: 'hidden',
                borderRadius: 16,
                padding: 24,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.25,
                shadowRadius: 24,
                elevation: 12,
              } as any}
            >
              {/* Gradient background matching Hero.tsx */}
              <LinearGradient
                colors={['rgba(200, 162, 200, 0.2)', 'rgba(135, 206, 235, 0.2)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 16,
                }}
              />
              <BlurView
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 16,
                } as any}
                blurType="light"
                blurAmount={10}
                reducedTransparencyFallbackColor="rgba(255, 255, 255, 0.9)"
              />
              <View style={{ position: 'relative', zIndex: 1 }}>
                <Title style={{
                  color: '#1a1a1a',
                  marginBottom: 20,
                  fontSize: 18,
                  fontWeight: '600',
                  lineHeight: 24,
                }}>
                  {currentInstruction.helpText}
                </Title>

                {/* Navigation buttons */}
                <XStack gap={10} justifyContent="space-between" alignItems="center">
                  <Button
                    buttonText="Previous"
                    customStyle={{ minHeight: 36, flex: 1 }}
                    defaultColor="gray"
                    textColor="white"
                    onPress={handlePrevious}
                    disabled={activeInstruction === 0}
                  />
                  <Button
                    buttonText={activeInstruction < instructions.length - 1 ? 'Next' : 'Got it!'}
                    customStyle={{ minHeight: 36, flex: 1 }}
                    defaultColor={activeInstruction < instructions.length - 1 ? 'blue' : 'green'}
                    textColor="white"
                    onPress={activeInstruction < instructions.length - 1 ? handleNext : handleComplete}
                  />
                </XStack>

                {/* Dismiss button */}
                <View style={{ marginTop: 12, alignItems: 'center' }}>
                  <Button
                    buttonText="Dismiss"
                    customStyle={{ minHeight: 32, maxWidth: '100%' }}
                    defaultColor="red"
                    textColor="white"
                    onPress={handleDismiss}
                  />
                </View>
              </View>
            </View>
          </Animatable.View>
        )}

        {/* Fallback message if element not found or not visible */}
        {currentInstruction && (!isElementVisible || !tooltipPosition) && (
          <View
            style={{
              position: 'absolute',
              bottom: 100,
              left: '50%',
              marginLeft: -150,
              zIndex: 10000,
              maxWidth: 300,
            } as any}
          >
            <View
              style={{
                overflow: 'hidden',
                borderRadius: 16,
                padding: 24,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.25,
                shadowRadius: 24,
                elevation: 12,
              } as any}
            >
              {/* Gradient background matching Hero.tsx */}
              <LinearGradient
                colors={['rgba(200, 162, 200, 0.2)', 'rgba(135, 206, 235, 0.2)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 16,
                }}
              />
              <BlurView
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 16,
                } as any}
                blurType="light"
                blurAmount={10}
                reducedTransparencyFallbackColor="rgba(255, 255, 255, 0.9)"
              />
              <View style={{ position: 'relative', zIndex: 1 }}>
                <Title style={{
                  color: '#1a1a1a',
                  marginBottom: 16,
                  fontSize: 18,
                  fontWeight: '600',
                  lineHeight: 24,
                }}>
                  {currentInstruction.helpText}
                </Title>
                <Text style={{ color: '#4a4a4a', fontSize: 14, marginBottom: 16, lineHeight: 20 }}>
                  {!tooltipPosition
                    ? `Looking for element: ${currentInstruction.targetElementId}. Please ensure the element exists with this ID.`
                    : 'Please scroll to find the highlighted element.'}
                </Text>
                {__DEV__ && (
                  <Text style={{ color: '#999', fontSize: 10, marginBottom: 8 }}>
                    Debug: Element ID: {currentInstruction.targetElementId}
                    {'\n'}Visible: {isElementVisible ? 'Yes' : 'No'}
                    {'\n'}Position: {tooltipPosition ? `${tooltipPosition.top}, ${tooltipPosition.left}` : 'Not calculated'}
                  </Text>
                )}

                <XStack gap={10} justifyContent="space-between" alignItems="center">
                  <Button
                    buttonText="Previous"
                    customStyle={{ minHeight: 36, flex: 1 }}
                    defaultColor="gray"
                    textColor="white"
                    onPress={handlePrevious}
                    disabled={activeInstruction === 0}
                  />
                  <Button
                    buttonText={activeInstruction < instructions.length - 1 ? 'Next' : 'Got it!'}
                    customStyle={{ minHeight: 36, flex: 1 }}
                    defaultColor={activeInstruction < instructions.length - 1 ? 'blue' : 'green'}
                    textColor="white"
                    onPress={activeInstruction < instructions.length - 1 ? handleNext : handleComplete}
                  />
              </XStack>

                <View style={{ marginTop: 12, alignItems: 'center' }}>
                  <Button
                    buttonText="Dismiss"
                    customStyle={{ minHeight: 32, maxWidth: '100%' }}
                    defaultColor="red"
                    textColor="white"
                    onPress={handleDismiss}
                  />
                </View>
              </View>
            </View>
          </View>
        )}
      </View>
    </Modal>
  );
};

export default FirstTutorial;
